import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen } from "lucide-react";
import { blogs } from "../data/blogs";
import Nav from "../components/landing/Nav";
import Footer from "../components/landing/Footer";

export default function BlogsPage({ onDemoClick }) {
  const [featured, ...rest] = blogs;
  return (
    <div className="relative min-h-screen bg-[#0a0a10] noise" data-testid="blogs-root">
      <Nav onDemoClick={onDemoClick} />
      <main className="pt-36 pb-20">
        <section className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-14 flex items-end justify-between flex-wrap gap-6">
            <div>
              <div className="inline-flex items-center gap-2 mb-5">
                <BookOpen className="w-3.5 h-3.5 text-[#7C5CFF]" />
                <span className="text-[11px] font-mono tracking-[0.2em] uppercase text-[#7C5CFF]">
                  ── Angoor journal
                </span>
              </div>
              <h1 className="font-serif text-5xl md:text-7xl leading-[0.98] tracking-tight text-white max-w-3xl">
                Notes on{" "}
                <span className="italic">agentic</span> customer experience.
              </h1>
              <p className="mt-5 text-white/55 text-lg max-w-2xl">
                Essays, product notes and opinions from the team building an
                AI-native Customer Interaction Platform.
              </p>
            </div>
            <div className="flex items-center gap-2 text-[11px] font-mono tracking-[0.14em] uppercase text-white/40">
              <span className="w-1.5 h-1.5 rounded-full bg-[#3DDFB3] soft-pulse" />
              {blogs.length} posts · updated weekly
            </div>
          </div>

          {/* Featured */}
          <Link
            to={`/blogs/${featured.slug}`}
            data-testid="blog-featured-card"
            className="group grid md:grid-cols-[1.2fr_1fr] gap-8 card-surface p-8 md:p-10 mb-10"
          >
            <div className="flex flex-col justify-between">
              <div>
                <span className="pill bg-[#7C5CFF]/15 border border-[#7C5CFF]/30 text-[#b9a9ff]">
                  Featured · {featured.category}
                </span>
                <h2 className="mt-6 font-serif text-3xl md:text-5xl leading-[1.05] text-white group-hover:text-white/90 transition-colors">
                  {featured.title}
                </h2>
                <p className="mt-5 text-white/55 text-[16px] leading-relaxed max-w-xl">
                  {featured.teaser}
                </p>
              </div>
              <div className="mt-8 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <img
                    src={featured.hero}
                    alt={featured.author}
                    className="w-9 h-9 rounded-full object-cover grayscale"
                  />
                  <div>
                    <div className="text-[13px] text-white">
                      {featured.author}
                    </div>
                    <div className="text-[11px] text-white/40">
                      {featured.date} · {featured.readTime}
                    </div>
                  </div>
                </div>
                <span className="inline-flex items-center gap-1.5 text-[13px] text-[#7C5CFF] group-hover:gap-3 transition-all">
                  Read essay <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </div>
            <BlogCover kind={featured.visual} title={featured.title} />
          </Link>

          {/* Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {rest.map((b, i) => (
              <Link
                to={`/blogs/${b.slug}`}
                key={b.slug}
                data-testid={`blog-card-${i}`}
                className="group card-surface p-6 flex flex-col"
              >
                <BlogCover kind={b.visual} title={b.title} compact />
                <div className="mt-6 flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.12em] text-white/45">
                  <span>{b.category}</span>
                  <span>·</span>
                  <span>{b.readTime}</span>
                </div>
                <h3 className="mt-3 font-serif text-[22px] leading-[1.2] text-white group-hover:text-white/90 transition-colors">
                  {b.title}
                </h3>
                <p className="mt-3 text-[13.5px] text-white/55 leading-relaxed line-clamp-3">
                  {b.teaser}
                </p>
                <div className="mt-5 pt-5 border-t border-white/5 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <img
                      src={b.hero}
                      alt={b.author}
                      className="w-7 h-7 rounded-full object-cover grayscale"
                    />
                    <span className="text-[12px] text-white/60">
                      {b.author}
                    </span>
                  </div>
                  <span className="text-[11px] text-white/40 font-mono">
                    {b.date}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

/**
 * Each blog card gets a small "mock UI" visual instead of just a plain cover image.
 * kind=flow | stack | split | chat
 */
export function BlogCover({ kind = "flow", compact = false, title = "" }) {
  const h = compact ? "h-36" : "h-full min-h-[280px]";
  if (kind === "chat") {
    return (
      <div
        className={`rounded-xl border border-white/10 bg-[#0b0b13] p-4 flex flex-col justify-between ${h} overflow-hidden`}
      >
        <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.12em] text-white/45">
          <span className="w-1.5 h-1.5 rounded-full bg-[#3DDFB3] soft-pulse" />
          live · whatsapp thread
        </div>
        <div className="space-y-2">
          <MiniBubble side="user">Can you help with my order?</MiniBubble>
          <MiniBubble side="ai">
            Found it — refunding ₹2,499 to •••4421 now.
          </MiniBubble>
          <div className="flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-[#7C5CFF] typing-dot" />
            <span className="w-1.5 h-1.5 rounded-full bg-[#7C5CFF] typing-dot" />
            <span className="w-1.5 h-1.5 rounded-full bg-[#7C5CFF] typing-dot" />
          </div>
        </div>
      </div>
    );
  }
  if (kind === "stack") {
    return (
      <div
        className={`rounded-xl border border-white/10 bg-[#0b0b13] p-4 flex flex-col gap-2 ${h} overflow-hidden`}
      >
        <div className="text-[10px] font-mono uppercase tracking-[0.12em] text-white/45 mb-1">
          customer · one record · every channel
        </div>
        {["whatsapp", "email", "web", "voice"].map((t, i) => (
          <div
            key={t}
            className="flex items-center justify-between px-3 py-2 rounded-lg bg-white/[0.02] border border-white/5 text-[11px] text-white/70"
          >
            <span className="font-mono">{t}</span>
            <span
              className="text-[10px] font-mono px-1.5 py-0.5 rounded"
              style={{
                background: i === 0 ? "rgba(61,223,179,0.1)" : "rgba(124,92,255,0.1)",
                color: i === 0 ? "#3DDFB3" : "#b9a9ff",
              }}
            >
              {i === 0 ? "active" : "+1d"}
            </span>
          </div>
        ))}
      </div>
    );
  }
  if (kind === "split") {
    return (
      <div
        className={`rounded-xl border border-white/10 bg-[#0b0b13] p-4 grid grid-cols-2 gap-3 ${h} overflow-hidden`}
      >
        <div className="rounded-lg border border-[#7C5CFF]/30 bg-[#7C5CFF]/10 p-3 flex flex-col">
          <div className="text-[10px] font-mono text-[#b9a9ff] uppercase tracking-[0.12em]">
            Microsoft
          </div>
          <div className="mt-auto text-[11px] text-white/70">
            "CRM is dead."
          </div>
        </div>
        <div className="rounded-lg border border-[#3DDFB3]/30 bg-[#3DDFB3]/10 p-3 flex flex-col">
          <div className="text-[10px] font-mono text-[#3DDFB3] uppercase tracking-[0.12em]">
            Salesforce
          </div>
          <div className="mt-auto text-[11px] text-white/70">
            "Agents need a system of record."
          </div>
        </div>
      </div>
    );
  }
  // default flow
  return (
    <div
      className={`relative rounded-xl border border-white/10 bg-[#0b0b13] ${h} overflow-hidden`}
    >
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,rgba(255,255,255,0.05)_1px,transparent_0)] [background-size:14px_14px]" />
      <svg
        className="absolute inset-0 w-full h-full pointer-events-none"
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
      >
        <path
          d="M 18 50 L 42 50"
          stroke="#7C5CFF"
          strokeOpacity="0.65"
          fill="none"
          className="flow-dash"
          vectorEffect="non-scaling-stroke"
          style={{ strokeWidth: 1.2 }}
        />
        <path
          d="M 58 50 L 82 50"
          stroke="#F97316"
          strokeOpacity="0.65"
          fill="none"
          className="flow-dash"
          vectorEffect="non-scaling-stroke"
          style={{ strokeWidth: 1.2 }}
        />
      </svg>
      <CoverNode x="4%" y="38%" color="#7C5CFF" label="Trigger" />
      <CoverNode x="40%" y="38%" color="#7C5CFF" label="Agent" />
      <CoverNode x="80%" y="38%" color="#3DDFB3" label="Resolve" />
      <div className="absolute bottom-3 left-3 text-[10px] font-mono uppercase tracking-[0.12em] text-white/40">
        flow · snapshot
      </div>
    </div>
  );
}

function CoverNode({ x, y, color, label }) {
  return (
    <div
      className="absolute rounded-md border px-2.5 py-1.5"
      style={{
        left: x,
        top: y,
        background: `${color}18`,
        borderColor: `${color}55`,
        boxShadow: `0 0 18px ${color}22`,
      }}
    >
      <div
        className="text-[11px] font-medium"
        style={{ color: "white" }}
      >
        {label}
      </div>
    </div>
  );
}

function MiniBubble({ side, children }) {
  const isAi = side === "ai";
  return (
    <div
      className={`inline-block max-w-[90%] px-2.5 py-1.5 rounded-lg text-[11px] leading-snug ${
        isAi
          ? "bg-[#7C5CFF]/12 border border-[#7C5CFF]/25 text-white/90"
          : "bg-white/[0.05] border border-white/5 text-white/85"
      }`}
    >
      {children}
    </div>
  );
}
